import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  HiCheckCircle,
  HiXCircle,
  HiMinusCircle,
  HiChevronDown,
  HiChevronRight,
  HiBanknotes,
  HiChartBar,
  HiCog,
  HiUserGroup,
  HiShieldCheck,
  HiLightBulb,
} from 'react-icons/hi2';

const BusinessHealthAssessmentForm = ({
  userRole,
  userId,
  onSave,
  initialData = null,
  isReadOnly = false,
}) => {
  const [formData, setFormData] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(true);

  const assessmentCategories = useMemo(
    () => [
      {
        id: 'strategy',
        title: 'Strategy & Vision',
        icon: <HiLightBulb className="h-5 w-5" />,
        questions: [
          {
            id: 'strategy_1',
            question: 'Do you have a clear business mission and vision?',
            category: 'strategy',
            type: 'yesNoPartial',
          },
          {
            id: 'strategy_2',
            question: 'Is there a strategic plan in place (1-3 years)?',
            category: 'strategy',
            type: 'yesNoPartial',
          },
          {
            id: 'strategy_3',
            question:
              'Are your business goals SMART (Specific, Measurable, Achievable, Relevant, Time-bound)?',
            category: 'strategy',
            type: 'yesNoPartial',
          },
          {
            id: 'strategy_4',
            question: "Do all team members understand and align with the company's direction?",
            category: 'strategy',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'financial',
        title: 'Financial Health',
        icon: <HiBanknotes className="h-5 w-5" />,
        questions: [
          {
            id: 'financial_1',
            question:
              'Do you regularly update and review financial statements (P&L, Balance Sheet, Cash Flow)?',
            category: 'financial',
            type: 'yesNoPartial',
          },
          {
            id: 'financial_2',
            question: 'Are you generating consistent profit and positive cash flow?',
            category: 'financial',
            type: 'yesNoPartial',
          },
          {
            id: 'financial_3',
            question: 'Do you have a budget and monitor it against actuals?',
            category: 'financial',
            type: 'yesNoPartial',
          },
          {
            id: 'financial_4',
            question: 'Are your debts manageable and well-structured?',
            category: 'financial',
            type: 'yesNoPartial',
          },
          {
            id: 'financial_5',
            question: 'Do you have access to emergency funds or credit?',
            category: 'financial',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'sales',
        title: 'Sales & Marketing',
        icon: <HiChartBar className="h-5 w-5" />,
        questions: [
          {
            id: 'sales_1',
            question: 'Do you have a defined target market and customer personas?',
            category: 'sales',
            type: 'yesNoPartial',
          },
          {
            id: 'sales_2',
            question: 'Is your value proposition clearly communicated?',
            category: 'sales',
            type: 'yesNoPartial',
          },
          {
            id: 'sales_3',
            question: 'Are you hitting your sales targets consistently?',
            category: 'sales',
            type: 'yesNoPartial',
          },
          {
            id: 'sales_4',
            question: 'Do you track marketing ROI and adjust campaigns accordingly?',
            category: 'sales',
            type: 'yesNoPartial',
          },
          {
            id: 'sales_5',
            question: 'Are you effectively using digital marketing and social media?',
            category: 'sales',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'operations',
        title: 'Operations & Processes',
        icon: <HiCog className="h-5 w-5" />,
        questions: [
          {
            id: 'operations_1',
            question: 'Are your business processes documented and efficient?',
            category: 'operations',
            type: 'yesNoPartial',
          },
          {
            id: 'operations_2',
            question: 'Is your supply chain reliable and cost-effective?',
            category: 'operations',
            type: 'yesNoPartial',
          },
          {
            id: 'operations_3',
            question: 'Are you leveraging technology to streamline operations?',
            category: 'operations',
            type: 'yesNoPartial',
          },
          {
            id: 'operations_4',
            question: 'Do you track key performance indicators (KPIs)?',
            category: 'operations',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'customer',
        title: 'Customer Experience',
        icon: <HiUserGroup className="h-5 w-5" />,
        questions: [
          {
            id: 'customer_1',
            question: 'Do you collect and act on customer feedback?',
            category: 'customer',
            type: 'yesNoPartial',
          },
          {
            id: 'customer_2',
            question: 'Is your customer service responsive and helpful?',
            category: 'customer',
            type: 'yesNoPartial',
          },
          {
            id: 'customer_3',
            question: 'Do you have a CRM system to manage relationships?',
            category: 'customer',
            type: 'yesNoPartial',
          },
          {
            id: 'customer_4',
            question: 'Is customer retention strong and improving?',
            category: 'customer',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'leadership',
        title: 'Leadership & Team',
        icon: <HiUserGroup className="h-5 w-5" />,
        questions: [
          {
            id: 'leadership_1',
            question: 'Does your team have clear roles and responsibilities?',
            category: 'leadership',
            type: 'yesNoPartial',
          },
          {
            id: 'leadership_2',
            question: 'Are performance reviews conducted regularly?',
            category: 'leadership',
            type: 'yesNoPartial',
          },
          {
            id: 'leadership_3',
            question: 'Do you invest in staff training and development?',
            category: 'leadership',
            type: 'yesNoPartial',
          },
          {
            id: 'leadership_4',
            question: 'Is the team culture positive and productive?',
            category: 'leadership',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'compliance',
        title: 'Compliance & Risk Management',
        icon: <HiShieldCheck className="h-5 w-5" />,
        questions: [
          {
            id: 'compliance_1',
            question: 'Are you compliant with all legal, tax, and regulatory obligations?',
            category: 'compliance',
            type: 'yesNoPartial',
          },
          {
            id: 'compliance_2',
            question: 'Do you have proper business insurance?',
            category: 'compliance',
            type: 'yesNoPartial',
          },
          {
            id: 'compliance_3',
            question: 'Are your contracts and agreements up to date?',
            category: 'compliance',
            type: 'yesNoPartial',
          },
          {
            id: 'compliance_4',
            question: 'Do you have a risk management plan?',
            category: 'compliance',
            type: 'yesNoPartial',
          },
        ],
      },
      {
        id: 'innovation',
        title: 'Innovation & Growth',
        icon: <HiLightBulb className="h-5 w-5" />,
        questions: [
          {
            id: 'innovation_1',
            question: 'Are you introducing new products/services or improving existing ones?',
            category: 'innovation',
            type: 'yesNoPartial',
          },
          {
            id: 'innovation_2',
            question: 'Do you track industry trends and emerging technologies?',
            category: 'innovation',
            type: 'yesNoPartial',
          },
          {
            id: 'innovation_3',
            question: 'Are you exploring new markets or partnerships?',
            category: 'innovation',
            type: 'yesNoPartial',
          },
          {
            id: 'innovation_4',
            question: 'Do you have an R&D or innovation budget?',
            category: 'innovation',
            type: 'yesNoPartial',
          },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      const initialFormData = {};
      assessmentCategories.forEach(category => {
        category.questions.forEach(question => {
          initialFormData[question.id] = {
            status: '',
            notes: '',
            lastUpdated: new Date().toISOString(),
          };
        });
      });
      setFormData(initialFormData);
    }
  }, [initialData, assessmentCategories]);

  const toggleCategory = categoryId => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleStatusChange = (questionId, status) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        status,
        lastUpdated: new Date().toISOString(),
      },
    }));
  };

  const handleNotesChange = (questionId, notes) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        notes,
        lastUpdated: new Date().toISOString(),
      },
    }));
  };

  const calculateCategoryScore = categoryId => {
    const categoryQuestions =
      assessmentCategories.find(cat => cat.id === categoryId)?.questions || [];
    const answeredQuestions = categoryQuestions.filter(q => formData[q.id]?.status !== '');
    const positiveAnswers = answeredQuestions.filter(q => formData[q.id]?.status === 'yes').length;
    const partialAnswers = answeredQuestions.filter(
      q => formData[q.id]?.status === 'partial'
    ).length;

    if (answeredQuestions.length === 0) return 0;

    return Math.round(((positiveAnswers + partialAnswers * 0.5) / answeredQuestions.length) * 100);
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    const answeredQuestions = Object.values(formData).filter(item => item.status !== '').length;
    const totalScore = assessmentCategories.reduce(
      (total, cat) => total + calculateCategoryScore(cat.id),
      0
    );

    if (answeredQuestions === 0) return 0;

    return Math.round(totalScore / assessmentCategories.length);
  };

  // Get status color
  const getStatusColor = status => {
    switch (status) {
      case 'yes':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'no':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'partial':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'yes':
        return <HiCheckCircle className="h-5 w-5" />;
      case 'no':
        return <HiXCircle className="h-5 w-5" />;
      case 'partial':
        return <HiMinusCircle className="h-5 w-5" />;
      default:
        return <HiMinusCircle className="h-5 w-5" />;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isReadOnly) return;

    setSaving(true);
    try {
      const assessmentData = {
        formData,
        overallScore: calculateOverallScore(),
        categoryScores: assessmentCategories.reduce((scores, cat) => {
          scores[cat.id] = calculateCategoryScore(cat.id);
          return scores;
        }, {}),
        lastUpdated: new Date().toISOString(),
        userId,
        userRole,
      };

      if (onSave) {
        await onSave(assessmentData);
      }

      toast.success('Business health assessment saved successfully!');
    } catch (error) {
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const overallScore = calculateOverallScore();

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-black md:text-2xl">Business Health Assessment</h2>
            <p className="text-gray-600">
              Comprehensive evaluation of your business across 8 key areas
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {showNotes ? 'Hide Notes' : 'Show Notes'}
            </button>
            {!isReadOnly && (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Assessment'}
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-3 md:p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900 md:text-lg">
                Overall Health Score
              </h3>
              <p className="text-xs text-gray-600 md:text-sm">Based on completed assessments</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600 md:text-3xl">{overallScore}%</div>
              <div className="text-xs text-gray-600 md:text-sm">
                {overallScore >= 80
                  ? 'Excellent'
                  : overallScore >= 60
                    ? 'Good'
                    : overallScore >= 40
                      ? 'Fair'
                      : overallScore >= 20
                        ? 'Poor'
                        : 'Critical'}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="space-y-4">
        {assessmentCategories.map((category, categoryIndex) => {
          const categoryScore = calculateCategoryScore(category.id);
          const isExpanded = expandedCategories[category.id];

          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full p-6 text-left hover:bg-gray-50"
                disabled={isReadOnly}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-blue-600">{category.icon}</div>
                    <div>
                      <h3 className="text-base font-semibold text-black md:text-lg">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {category.questions.length} questions •{' '}
                        {category.questions.filter(q => formData[q.id]?.status !== '').length}{' '}
                        answered
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{categoryScore}%</div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                    {isExpanded ? (
                      <HiChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-gray-100"
                  >
                    <div className="p-6">
                      <div className="mb-4 grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                        <div className="col-span-6">Question</div>
                        <div className="col-span-2 text-center">Status</div>
                        {showNotes && <div className="col-span-4">Notes/Action Required</div>}
                      </div>

                      <div className="space-y-4">
                        {category.questions.map(question => (
                          <div key={question.id} className="grid grid-cols-12 items-start gap-4">
                            <div className="col-span-6">
                              <p className="text-sm text-gray-900">{question.question}</p>
                            </div>

                            <div className="col-span-2">
                              {!isReadOnly ? (
                                <div className="flex flex-col gap-2">
                                  {['yes', 'partial', 'no'].map(status => (
                                    <button
                                      key={status}
                                      onClick={() => handleStatusChange(question.id, status)}
                                      className={`flex items-center justify-center gap-2 rounded-lg border p-2 text-xs font-medium transition-all hover:scale-105 ${
                                        formData[question.id]?.status === status
                                          ? getStatusColor(status)
                                          : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
                                      }`}
                                    >
                                      {getStatusIcon(status)}
                                      {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div
                                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${getStatusColor(formData[question.id]?.status || '')}`}
                                >
                                  {getStatusIcon(formData[question.id]?.status || '')}
                                  {formData[question.id]?.status
                                    ? formData[question.id].status.charAt(0).toUpperCase() +
                                      formData[question.id].status.slice(1)
                                    : 'Not Answered'}
                                </div>
                              )}
                            </div>

                            {showNotes && (
                              <div className="col-span-4">
                                {!isReadOnly ? (
                                  <textarea
                                    value={formData[question.id]?.notes || ''}
                                    onChange={e => handleNotesChange(question.id, e.target.value)}
                                    placeholder="Add notes, actions, or observations..."
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    rows={2}
                                  />
                                ) : (
                                  <div className="min-h-[40px] rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                    {formData[question.id]?.notes || 'No notes added'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-lg font-semibold text-black">Assessment Summary</h3>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {assessmentCategories.map(category => (
            <div key={category.id} className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {calculateCategoryScore(category.id)}%
              </div>
              <div className="text-sm text-gray-600">{category.title}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default BusinessHealthAssessmentForm;
